"use client";
import { toastNotify } from "@client/components/ui/toast";
import { useTranslations } from "next-intl";
import { useState, useEffect, useCallback, useRef } from "react";
import type { LocalUserChoices } from "@livekit/components-react";
import { Room, RoomEvent, Track, TrackPublication } from "livekit-client";
import { MediaStorage } from "@shared/utils";
import { Logger } from "@shared/utils/logger";

interface UseMediaControlsOptions {
  room: Room;
  userChoices: LocalUserChoices;
}

interface MediaState {
  mic: boolean;
  camera: boolean;
  screenShare: boolean;
}

interface UseMediaControlsReturn {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenSharing: boolean;
  toggleMic: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
}

// Error patterns for quick lookup
const PERMISSION_ERROR_PATTERNS = ["permission denied", "notallowederror", "notfounderror"] as const;
const CANCELLED_ERROR_NAMES = new Set(["notallowederror", "aborterror"]);
const CANCELLED_ERROR_PATTERNS = [
  "aborted",
  "canceled",
  "platform",
  "cancelled",
  "user agent",
  "user cancelled",
  "denied by user"
] as const;

const isPermissionError = (message: string): boolean => {
  const lowered = message.toLowerCase();
  return PERMISSION_ERROR_PATTERNS.some((p) => lowered.includes(p));
};

const isUserCancelledError = (error: Error): boolean => {
  if (isPermissionError(error.message)) return false;
  if (CANCELLED_ERROR_NAMES.has(error.name.toLowerCase())) return true;
  const lowered = error.message.toLowerCase();
  return CANCELLED_ERROR_PATTERNS.some((p) => lowered.includes(p));
};

const promptMediaPermission = async (kind: "audio" | "video"): Promise<void> => {
  if (!navigator.mediaDevices?.getUserMedia) return;
  const constraints = kind === "audio" ? { audio: true } : { video: true };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  stream.getTracks().forEach((track) => track.stop());
};

/**
 * Manages media controls (camera, microphone, screen share) for the local participant.
 */
export const useMediaControls = ({ room, userChoices }: UseMediaControlsOptions): UseMediaControlsReturn => {
  const t = useTranslations();
  // Combined state to reduce re-renders - use lazy initializer
  const [mediaState, setMediaState] = useState<MediaState>(() => ({
    mic: userChoices?.audioEnabled,
    camera: userChoices?.videoEnabled,
    screenShare: false
  }));

  // Stable refs to avoid recreating callbacks
  const roomRef = useRef(room);
  const mediaStateRef = useRef(mediaState);
  roomRef.current = room;
  mediaStateRef.current = mediaState;

  // Consolidated effect for track state sync
  useEffect(() => {
    const localParticipant = room.localParticipant;

    const updateTrackStates = (publication?: TrackPublication) => {
      // Update mic state
      if (!publication || publication.source === Track.Source.Microphone) {
        const micEnabled = localParticipant.isMicrophoneEnabled;
        setMediaState((prev) => (prev.mic === micEnabled ? prev : { ...prev, mic: micEnabled }));
      }

      // Update camera state
      if (!publication || publication.source === Track.Source.Camera) {
        const cameraEnabled = localParticipant.isCameraEnabled;
        setMediaState((prev) => (prev.camera === cameraEnabled ? prev : { ...prev, camera: cameraEnabled }));
      }

      // Update screen share state
      if (!publication || publication.source === Track.Source.ScreenShare) {
        const screenShareEnabled = localParticipant.isScreenShareEnabled;
        setMediaState((prev) =>
          prev.screenShare === screenShareEnabled ? prev : { ...prev, screenShare: screenShareEnabled }
        );
      }
    };

    // Set initial states
    updateTrackStates();

    // Single handler for all track events
    const handleTrackChange = (pub: TrackPublication) => updateTrackStates(pub);

    // Subscribe to events once
    localParticipant.on(RoomEvent.TrackMuted, handleTrackChange);
    localParticipant.on(RoomEvent.TrackUnmuted, handleTrackChange);
    localParticipant.on(RoomEvent.LocalTrackPublished, handleTrackChange);
    localParticipant.on(RoomEvent.LocalTrackUnpublished, handleTrackChange);

    return () => {
      localParticipant.off(RoomEvent.TrackMuted, handleTrackChange);
      localParticipant.off(RoomEvent.TrackUnmuted, handleTrackChange);
      localParticipant.off(RoomEvent.LocalTrackPublished, handleTrackChange);
      localParticipant.off(RoomEvent.LocalTrackUnpublished, handleTrackChange);
    };
  }, [room]);

  // Stable callbacks using refs - no dependencies needed
  const toggleMic = useCallback(async () => {
    const lp = roomRef.current.localParticipant;
    const newState = !mediaStateRef.current.mic;
    try {
      if (newState) {
        await promptMediaPermission("audio");
      }
      await lp.setMicrophoneEnabled(newState);
      MediaStorage.savePreferences(newState, mediaStateRef.current.camera);
      toastNotify.success(newState ? t("participant.unmute") : t("participant.mute"));
    } catch (error) {
      if (error instanceof Error && !isUserCancelledError(error)) {
        Logger.error("useMediaControls", "Error toggling microphone:", error);
        toastNotify[isPermissionError(error.message) ? "warning" : "error"](
          isPermissionError(error.message) ? t("errors.micPermissionDenied") : t("errors.micToggleFailed")
        );
      }
    }
  }, [t]);

  const toggleCamera = useCallback(async () => {
    const lp = roomRef.current.localParticipant;
    const newState = !mediaStateRef.current.camera;
    try {
      if (newState) {
        await promptMediaPermission("video");
      }
      await lp.setCameraEnabled(newState);
      MediaStorage.savePreferences(mediaStateRef.current.mic, newState);
    } catch (error) {
      if (error instanceof Error && !isUserCancelledError(error)) {
        Logger.error("useMediaControls", "Error toggling camera:", error);
        toastNotify[isPermissionError(error.message) ? "warning" : "error"](
          isPermissionError(error.message) ? t("errors.cameraPermissionDenied") : t("errors.cameraToggleFailed")
        );
      }
    }
  }, [t]);

  const toggleScreenShare = useCallback(async () => {
    const lp = roomRef.current.localParticipant;
    const newState = !mediaStateRef.current.screenShare;
    try {
      // Request the browser's display-audio track as well as the video track.
      // Chrome will then show "Share tab audio" / "Share system audio" in the
      // native screen-share picker when the selected surface supports it.
      await lp.setScreenShareEnabled(newState, newState ? { audio: true } : undefined);
    } catch (error) {
      if (error instanceof Error && !isUserCancelledError(error)) {
        Logger.error("useMediaControls", "Error toggling screen share:", error);
        toastNotify.error(t("errors.screenShareFailed"));
      }
    }
  }, [t]);

  return {
    isMicEnabled: mediaState.mic,
    isCameraEnabled: mediaState.camera,
    isScreenSharing: mediaState.screenShare,
    toggleMic,
    toggleCamera,
    toggleScreenShare
  };
};
